import { createStore } from '@xstate/store';
import type { BoardTaskViewsData } from '../../types';
import type { BoardTaskViewContext } from '../../stores/types';
import { createDebouncedPersistence } from '../../stores/storePersistence';

// ============================================
// Board Task View Store - Per Board (Factory Pattern)
// ============================================

function getLocalStorageKey(boardId: string): string {
  return `roadmap_2026_board_${boardId}_task_views`;
}

function loadViewsFromLocalStorage(storageKey: string): BoardTaskViewsData {
  try {
    const saved = localStorage.getItem(storageKey);
    if (!saved) return {};

    const parsed = JSON.parse(saved) as Record<string, { column: string; aussortiert?: boolean }>;
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return {};

    // Migration: remove entries with aussortiert: true (task leaves board)
    // and strip aussortiert field from remaining entries
    const migrated: BoardTaskViewsData = {};
    for (const [taskId, view] of Object.entries(parsed)) {
      if (view.aussortiert) continue; // Task was aussortiert -> remove from board
      migrated[taskId] = { column: view.column };
    }
    return migrated;
  } catch {
    return {};
  }
}

function createBoardTaskViewStoreInstance(storageKey: string) {
  const initialViews = loadViewsFromLocalStorage(storageKey);

  const store = createStore({
    context: {
      views: initialViews,
    } as BoardTaskViewContext,

    on: {
      'board.addTask': (ctx, event: { taskId: string; column: string }) => {
        if (ctx.views[event.taskId]) return ctx;
        return {
          ...ctx,
          views: {
            ...ctx.views,
            [event.taskId]: {
              column: event.column,
            },
          },
        };
      },

      'board.removeTask': (ctx, event: { taskId: string }) => {
        if (!ctx.views[event.taskId]) return ctx;
        const { [event.taskId]: _, ...remainingViews } = ctx.views;
        return {
          ...ctx,
          views: remainingViews,
        };
      },

      'board.moveTaskColumn': (ctx, event: { taskId: string; column: string }) => {
        const view = ctx.views[event.taskId];
        if (!view) return ctx;
        return {
          ...ctx,
          views: {
            ...ctx.views,
            [event.taskId]: { ...view, column: event.column },
          },
        };
      },

      'board.importViews': (_ctx, event: { views: BoardTaskViewsData }) => ({
        views: event.views,
      }),

      'board.resetViews': () => ({
        views: {},
      }),
    },
  });

  // Debounced persistence
  const viewPersistence = createDebouncedPersistence<BoardTaskViewContext>(
    storageKey,
    (ctx) => JSON.stringify(ctx.views),
    (ctx) => Object.entries(ctx.views)
      .map(([id, v]) => `${id}:${v.column}`)
      .join('|'),
  );

  store.subscribe((snapshot) => {
    viewPersistence.save(snapshot.context);
  });

  return store;
}

// ============================================
// Board-specific Store Instances
// ============================================

type BoardTaskViewStoreInstance = ReturnType<typeof createBoardTaskViewStoreInstance>;

const storeInstances = new Map<string, BoardTaskViewStoreInstance>();

export function initBoardTaskViewStore(boardId: string): BoardTaskViewStoreInstance {
  const existing = storeInstances.get(boardId);
  if (existing) return existing;

  const storageKey = getLocalStorageKey(boardId);
  const store = createBoardTaskViewStoreInstance(storageKey);
  storeInstances.set(boardId, store);
  return store;
}

export function getBoardTaskViewStore(boardId: string): BoardTaskViewStoreInstance {
  const instance = storeInstances.get(boardId);
  if (!instance) {
    throw new Error(`Board task view store for board "${boardId}" not initialized. Call initBoardTaskViewStore first.`);
  }
  return instance;
}

export function disposeBoardTaskViewStore(boardId: string): void {
  storeInstances.delete(boardId);
}

// ============================================
// Helper Functions
// ============================================

export function getBoardTaskViews(boardId: string): BoardTaskViewsData {
  return getBoardTaskViewStore(boardId).getSnapshot().context.views;
}

export function getBoardTaskView(boardId: string, taskId: string): { column: string } | undefined {
  return getBoardTaskViews(boardId)[taskId];
}

export type BoardTaskViewStore = BoardTaskViewStoreInstance;
